/**
 * Las piezas comunes de las barreras de entrada de ruta: resolver ANTES de montar la pantalla lo
 * que la pantalla necesita saber para no empezar suponiendo.
 *
 * Salieron de `barreraBloqueoVacaciones.js` (#49) y están aquí porque la #65 necesita exactamente
 * el mismo baile para otra cosa —el usuario y sus permisos— y copiarlo habría dejado dos
 * mecanismos de arranque distintos, que es justo lo que la decisión de diseño de Donchurru del
 * 01/10/2026 pide evitar. El comportamiento no cambia: es el mismo código, con un dueño.
 *
 * Lo que una barrera garantiza, y por qué cada pieza:
 *
 * - **Nada se monta con el dato a medias.** Cuando el componente nace, el valor ya está decidido y
 *   viaja como `prop`. No hay ventana de «todavía no se sabe» que cada escritor tenga que tapar
 *   por su cuenta.
 * - **Un fallo no equivale a un valor por defecto** (`alFallar`): error, timeout o cuerpo ilegible
 *   enseñan una pantalla de error con reintento, no la pantalla con lo que se suponía.
 * - **El router no se queda congelado** (`liberarRouter`): F7 cierra `allowPageChange` justo antes
 *   de llamar al resolvedor `async` y descarta EN SILENCIO cualquier navegación mientras tanto,
 *   incluido el `navigate('/login/')` de un 401.
 * - **Las respuestas tardías se descartan** (`escucharSalida` + número de navegación): tanto si el
 *   usuario se fue a otra ruta como si volvió a entrar en la misma.
 *
 * [#59, 07/10/2026] La cuenta de las consultas en vuelo que llevaba este módulo —para que la
 * respuesta descartada de una navegación no apagara el indicador de la siguiente— vive ahora en
 * `utils/esperaGlobal.js`, porque el preloader que apaga es uno solo para toda la aplicación y las
 * esperas de dentro de las pantallas (`preloaderDeLaSesion`) tenían la suya aparte: dos cuentas de
 * un mismo modal se pisan entre ellas. El comportamiento de las barreras no cambia.
 *
 * [#59, 17/10/2026] Y esa espera es ahora un **testigo** de esta navegación en vez de un apunte en
 * una cuenta: lo suelta `cerrar`, que ya era idempotente, y soltarlo cuando ya no está no apaga
 * nada. Era lo que faltaba para que una respuesta tardía no pudiera apagar el indicador de la
 * navegación siguiente ni el de una pantalla que siga cargando.
 */
import { pedirEspera, soltarEspera } from './utils/esperaGlobal.js';
import { comoPromesaComun, esCancelacion } from './utils/sesionDePeticiones.js';

/** Cuánto se espera al servidor antes de dar la consulta por fallida. */
export const TOPE_BARRERA_MS = 10000;

export function appDeRouter(router) {
    if (router && router.app) return router.app;
    // `window.app` es el mismo respaldo que usa DatabaseModel.sendRequest cuando no tiene router.
    return (typeof window != 'undefined' && window.app) || null;
}

/**
 * La consulta con tope de tiempo. Se resuelve una sola vez: lo que llegue después del tope se
 * ignora, porque para entonces ya se ha enseñado el error y quien lo mire pulsará «Reintentar».
 *
 * Devuelve además `cancelar()`, que apaga el temporizador y deja la promesa sin resolver. Hace
 * falta porque el temporizador es lo que sobrevive a la navegación: sin apagarlo, diez segundos
 * después de que el usuario se haya ido seguiría saltando para contestar una ruta que ya no existe.
 *
 * @param {Function} pedir devuelve la promesa de la consulta
 * @param {Number} tope milisegundos
 */
export function consultaConTope(pedir, tope) {
    let terminado = false;
    let temporizador = null;
    const promesa = new Promise(function(resolve, reject) {
        temporizador = setTimeout(function() {
            if (terminado) return;
            terminado = true;
            reject({ message: 'El servidor no ha contestado a tiempo.' });
        }, tope);
        // #663 · Como promesa común: si la sesión cambia mientras espera, la frontera calla las
        // continuaciones, y sin estas el temporizador seguía corriendo para acabar contestando
        // «no ha contestado a tiempo» en la sesión siguiente. Así termina con la cancelación.
        comoPromesaComun(pedir()).then(function(valor) {
            if (terminado) return;
            terminado = true;
            clearTimeout(temporizador);
            resolve(valor);
        }, function(e) {
            if (terminado) return;
            terminado = true;
            clearTimeout(temporizador);
            reject(e);
        });
    });
    return {
        promesa: promesa,
        cancelar: function() {
            if (terminado) return;
            terminado = true;
            clearTimeout(temporizador);
        }
    };
}

/**
 * Suelta el cerrojo de navegación que Framework7 cierra justo antes de llamarnos.
 *
 * `navigate.js` hace `router.allowPageChange = false` en la línea anterior a invocar el resolvedor
 * `async` de la ruta, y lo deja así hasta que se conteste. Mientras tanto `router.load()` descarta
 * EN SILENCIO cualquier navegación (`if (!router.allowPageChange && !ignorePageChange) return`).
 *
 * O sea que, sin esto, no es que la salida se detecte tarde: es que no puede ocurrir. Durante los
 * hasta diez segundos de la consulta, ni el menú lateral ni el botón de atrás responden, y tampoco
 * el `navigate('/login/')` que hace `removeSession()` al recibir un 401, que se pierde dejando al
 * usuario con la sesión ya borrada y atrapado en la pantalla anterior hasta que salta el tope.
 *
 * Soltarlo es seguro: cuando contestamos, F7 carga la pantalla con `ignorePageChange = true`
 * (`asyncResolve` llama a `router.load(..., true)`), así que no vuelve a mirar este cerrojo.
 */
export function liberarRouter(router) {
    if (router) router.allowPageChange = true;
}

/**
 * Ata la vigencia de la consulta al ciclo real del router: en cuanto el router cambia a otra ruta
 * —cualquiera: el menú, el botón de atrás, el login al que redirige un 401— esta navegación queda
 * abandonada y su respuesta no debe montar nada.
 *
 * Se escucha `routeChange`, que F7 emite al asignar `router.currentRoute` (`router-class.js`), y no
 * una lista de salidas conocidas: la gracia es precisamente no tener que enumerarlas.
 *
 * Devuelve la función de baja, o `null` si no había a quién escuchar: quien la guarde comprueba
 * antes de llamarla (ver `cerrar` en `crearBarrera`).
 */
export function escucharSalida(router, urlDestino, alSalir) {
    if (!router || typeof router.on != 'function') return null;
    function alCambiarDeRuta(nuevaRuta) {
        // Montar ESTA ruta también emite `routeChange`. Para entonces ya hemos dejado de escuchar,
        // pero comparar la url lo deja explícito y protege de cualquier otro emisor.
        if (nuevaRuta && nuevaRuta.url === urlDestino) return;
        alSalir();
    }
    router.on('routeChange', alCambiarDeRuta);
    return function() {
        if (typeof router.off == 'function') router.off('routeChange', alCambiarDeRuta);
    };
}

/**
 * Número de serie de la última entrada por una barrera, guardado EN EL ROUTER.
 *
 * Lo que necesita descartarse es la respuesta de una navegación abandonada POR OTRA ENTRADA a una
 * pantalla con barrera: esa no cambia la ruta del router hasta que se monta, así que
 * `escucharSalida` no la ve y hace falta contarla. Se compara por identidad del número, no por URL:
 * volver a la MISMA url es también una navegación nueva.
 *
 * Va en el router y no en una variable del módulo porque la aplicación tiene varias vistas con
 * router propio (`main`, `left`): con un contador único, abrir el menú lateral invalidaría una
 * entrada de la vista principal que sigue siendo la buena.
 */
export function nuevaNavegacion(router) {
    if (!router) return 0;
    router.__barreraNavegacion = (router.__barreraNavegacion || 0) + 1;
    return router.__barreraNavegacion;
}

/** ¿Sigue mandando esta navegación, o la ha sustituido otra entrada por una barrera? */
export function navegacionVigente(router, navegacion) {
    if (!router) return true;
    return (router.__barreraNavegacion || 0) === navegacion;
}

export function textoDelFallo(e, porDefecto) {
    const generico = porDefecto || 'No se ha podido completar la comprobación.';
    if (!e) return generico;
    if (typeof e == 'string') return e;
    return e.message || e.error || e.error_description || generico;
}

function contestar(resolve, pantalla) {
    if (!pantalla || !pantalla.component) return;
    resolve({ component: pantalla.component }, { props: pantalla.props || {} });
}

/**
 * Construye el resolvedor `async` de una ruta con barrera.
 *
 * @param {Object} config
 * @param {Function} [config.antes] `(routeTo) => {component, props}` o nada. Atajo inmediato, sin
 *        consultar: lo usa quien puede denegar el acceso sin preguntarle nada al servidor.
 * @param {Function} config.pedir `(routeTo) => Promise<valor>` lo que hay que saber antes de montar.
 * @param {Function} config.alResolver `(valor, routeTo) => {component, props}` la pantalla buena.
 * @param {Function} config.alFallar `(error, routeTo) => {component, props}` la pantalla de error.
 * @param {Number} [config.tope] milisegundos; por defecto `TOPE_BARRERA_MS`.
 */
export function crearBarrera(config) {
    // Framework7 llama al resolvedor con `(routeTo, routeFrom, resolve, reject)`. El `reject` no se
    // recoge porque ya no se usa: una navegación abandonada se cierra sin contestarle al router, y
    // eso es seguro desde que `liberarRouter` dejó de retenerlo.
    return function(routeTo, routeFrom, resolve) {
        const router = this;
        const app = appDeRouter(router);

        const atajo = config.antes ? config.antes(routeTo) : null;
        if (atajo) {
            contestar(resolve, atajo);
            return;
        }

        const navegacion = nuevaNavegacion(router);

        // Antes de nada: devolverle al router la libertad de navegar mientras esperamos. Si no, la
        // salida que este código quiere detectar no llega a ocurrir nunca (ver `liberarRouter`).
        liberarRouter(router);

        // El testigo de la espera de ESTA navegación: lo suelta su `cerrar` y nadie más. Mientras
        // una barrera espera, otra pantalla puede haber pedido el suyo, y apagar el modal entre las
        // dos es lo que tenía que dejar de poder pasar (ver `esperaGlobal`).
        const espera = pedirEspera(app);

        const consulta = consultaConTope(function() {
            return config.pedir(routeTo);
        }, config.tope || TOPE_BARRERA_MS);
        // Se asigna unas líneas más abajo, cuando ya existe `cerrar` al que avisar.
        let dejarDeEscuchar = null;
        let cerrada = false;

        /**
         * Cierra esta navegación: apaga el temporizador, deja de escuchar al router y quita el
         * indicador de espera. Idempotente, y devuelve `true` solo la primera vez — quien lo llame
         * usa ese `true` para saber si todavía le toca contestarle al router.
         */
        function cerrar() {
            if (cerrada) return false;
            cerrada = true;
            if (dejarDeEscuchar) dejarDeEscuchar();
            consulta.cancelar();
            soltarEspera(espera);
            return true;
        }

        // Salirse cierra la consulta y no contesta nada. No hace falta `reject()`: ya hemos soltado
        // el cerrojo del router, así que no retenemos ninguna navegación, y llamarlo ahora lo
        // soltaría otra vez en mitad del montaje de la pantalla a la que se ha ido el usuario.
        dejarDeEscuchar = escucharSalida(router, routeTo.url, cerrar);

        consulta.promesa.then(function(valor) {
            if (!cerrar() || !navegacionVigente(router, navegacion)) return;
            contestar(resolve, config.alResolver(valor, routeTo));
        }, function(e) {
            // La consulta de otra sesión se cierra sin contestar: su fallo no es de quien esté.
            if (!cerrar() || esCancelacion(e) || !navegacionVigente(router, navegacion)) return;
            contestar(resolve, config.alFallar(e, routeTo));
        });
    };
}

export default crearBarrera;
